import { apiClient } from "../../../../services/apiClient";

const variantSelectorExtendedApi = {
  /**
   * Get extended variant data with activity sequences.
   * Calls the new /variant-display-extended endpoint.
   *
   * @param {string} objectType - Object type to fetch variants for
   * @param {string} activeFile - Active file type (default: 'main')
   * @param {Object} globalFilterRequest - Global filters to apply
   * @param {Object} activityFilter - Activity filter configuration
   * @param {string} variantMode - Variant grouping mode ("exact" | "compressed" | "loop_compressed")
   * @param {string} repetitionDisplay - Loop multiplier rendering ("exact" | "abstract").
   *   Only meaningful when variantMode is "loop_compressed".
   * @returns {Promise<Object>} - Response with variants, activity_sequence, activity_catalog
   */
  getExtendedVariants: async (
    objectType,
    activeFile = null,
    globalFilterRequest = {},
    activityFilter = null,
    variantMode = null,
    repetitionDisplay = null,
  ) => {
    try {
      console.log(`Getting extended variants for object type: ${objectType} with filters:`, globalFilterRequest);

      // Build request with global filters excluding variant filters
      const requestData = {
        object_type: objectType,
        selected_variant_ids: [],
        ...globalFilterRequest
      };

      // Add activity filter if provided
      if (activityFilter) {
        requestData.activity_filter = activityFilter;
        console.log(`Activity filter applied:`, activityFilter);
      }

      // Forward the selected variant grouping mode so the backend can return
      // exact, consecutive-collapsed, or loop-collapsed groupings.
      if (variantMode) {
        requestData.variant_mode = variantMode;
      }

      if (repetitionDisplay) {
        requestData.repetition_display = repetitionDisplay;
      }

      const response = await apiClient.post("/variant-display-extended", requestData);

      if (
        response &&
        typeof response.data === "object" &&
        response.data !== null
      ) {
        console.log("Extended variants fetched successfully.");
        return response.data;
      } else {
        throw new Error("Failed to fetch extended variants.");
      }
    } catch (error) {
      console.error(
        "Error fetching extended variants:",
        error.response || error.message || error
      );
      throw error;
    }
  },

  /**
   * Filter OCEL by selected variants.
   * Reuses the existing endpoint from the original VariantSelector.
   */
  filterOCELByVariants: async (objectType, selectedObjectIds, activeFile) => {
    if (!activeFile) {
      throw new Error('filterOCELByVariants: activeFile is required');
    }
    try {
      const response = await apiClient.post("/filter-ocel-by-variants", {
        file_type: activeFile,
        object_type: objectType,
        selected_object_ids: selectedObjectIds,
      });

      if (response && response.data?.status === "success") {
        console.log("OCEL successfully filtered.");
        return response.data;
      } else {
        throw new Error(
          "Filtering OCEL failed or returned unexpected response."
        );
      }
    } catch (error) {
      console.error(
        "Error filtering OCEL:",
        error.response || error.message || error
      );
      throw error;
    }
  },
};

export default variantSelectorExtendedApi;
